import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IconComponent } from '../../../core/components/icon/icon.component';
import { FromDictionaryPipe } from '@teamfoster/sdk/dictionary-ngrx';
import { isPlatformBrowser } from '@angular/common';
import { ClarityService } from 'src/app/core/service/clarity.service';

@Component({
  selector: 'app-product-amount',
  templateUrl: './product-amount.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, FormsModule, ReactiveFormsModule, FromDictionaryPipe],
})
export class ProductAmountComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  private clarity = inject(ClarityService);

  @Input() suffix: string = '';
  @Input() max: number = 0;
  @Input() min: number = 1;
  @Input() maxReached: boolean = false; // -1 = not available, 0 = keep selling even if out of stock
  @Input() showLabel: boolean = true;
  @Input() showMaxLabel: boolean = false;
  @Input() prefix: string = 'product-aantal';

  private _amount = 1;

  @Input() set amount(value: number) {
    this.amountField.setValue(this.amount, { emitEvent: false });
    this._amount = value;
  }

  get amount() {
    return this._amount;
  }

  updatedAmountToMaxAvailable = false;

  @Output() getSelectedAmount = new EventEmitter<number>();

  amountField = this.fb.control(this.amount, [Validators.required, Validators.max(this.max), Validators.min(this.min)]);

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['max']) {
      // check if there is an max quantity
      if (this.max === -1) {
        this.maxReached = true;
      } else if (this.max === 0) {
        this.maxReached = false;
      } else {
        this.maxReached = this.amount >= this.max;
      }

      if (this.amount > this.max && this.max > 0) {
        this.amount = this.max;
        this.amountField.setValue(this.amount, { emitEvent: false });
        this.updatedAmountToMaxAvailable = true;
      } else {
        this.updatedAmountToMaxAvailable = false;
      }

      this.amountField.setValidators([Validators.required, Validators.max(this.max), Validators.min(this.min)]);
    }
  }

  get disabled() {
    return this.max === -1;
  }

  ngOnInit(): void {
    if (this.max === -1) {
      this.maxReached = true;
    }
  }

  add() {
    if (this.max === 0) {
      this.amount++;
      this.amountField.setValue(this.amount, { emitEvent: false });
      this.getSelectedAmount.emit(this.amount);
      return;
    }

    if (this.amount < this.max) {
      this.amount++;
      this.amountField.setValue(this.amount, { emitEvent: false });
    }

    if (this.amount === this.max) {
      this.maxReached = true;
    } else {
      this.maxReached = false;
    }

    this.getSelectedAmount.emit(this.amount);
    this.clarity.setClarityTag('interaction_type', 'quantity_stepper');
  }

  subtract() {
    if (this.amount > this.min) {
      this.amount--;
      this.maxReached = false;
      this.amountField.setValue(this.amount, { emitEvent: false });
    }

    this.getSelectedAmount.emit(this.amount);
    this.clarity.setClarityTag('interaction_type', 'quantity_stepper');
  }

  amountChange(amount: number) {
    this.amount = amount;

    if (this.amount > this.max) {
      this.amount = this.max;
    }
    if (this.amount < this.min) {
      this.amount = this.min;
    }

    if (this.amount === this.max) {
      this.maxReached = true;
    } else {
      this.maxReached = false;
    }

    this.amountField.setValue(this.amount, { emitEvent: false });
    this.getSelectedAmount.emit(this.amount);
  }
}
